import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { formatDateTime } from '../../utils/dateTime';

// UI Components
import { LayoutTemplate, Settings, Database, Plus, PanelRightClose, PanelRightOpen, ArrowRightFromLine, ArrowLeftFromLine, ChevronRight, ChevronLeft, Lightbulb, Trash2, Move, History, Lock, AlertTriangle, Loader2, FileText, X, Copy, ArrowLeft, Share2, Maximize, Minimize, ChevronDown } from 'lucide-react';
import {
    DropdownMenu as ViewDropdownMenu,
    DropdownMenuContent as ViewDropdownMenuContent,
    DropdownMenuItem as ViewDropdownMenuItem,
    DropdownMenuTrigger as ViewDropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { WidgetToolbox } from '@/components/DashboardStudio/WidgetToolbox';
import { DataPanel } from '@/components/DashboardStudio/DataPanel';
import { DashboardHeader } from '@/components/DashboardStudio/DashboardHeader';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { WidgetRenderer } from '@/components/DashboardStudio/WidgetRenderer';
import { FilterWidget } from '@/components/DashboardStudio/FilterWidget';
import { HeaderSettingsModal } from '@/components/DashboardStudio/HeaderSettingsModal';
import { DataPreviewModal } from '@/components/DashboardStudio/DataPreviewModal';
import { DataModelPreviewTabsModal } from '@/components/DashboardStudio/DataModelPreviewTabsModal';
import { MeasureWizardModal } from '@/components/DashboardStudio/MeasureWizardModal';
import { DashboardVersionHistoryPanel } from '@/components/DashboardStudio/DashboardVersionHistoryPanel';
import { DashboardShareSheet } from '@/components/DashboardStudio/DashboardShareSheet';
import { resolveColorTheme, getWidgetLayoutStyle, type ColorThemeId, type LayoutId } from '@/lib/dashboardThemes';
import { useData } from '@/hooks/useData';


import { apiFetch } from '@/api';
import { ThemeContext } from '@/context/ThemeContext';
import { useNotification } from '@/context/NotificationContext';
import { useAuth } from '@/context/useAuth';
import PublishAppModal from '@/components/flows/PublishAppModal';
import Watermark from '@/components/Watermark.jsx';
import pkg from '../../../package.json';
const BUILD_VERSION = pkg.version;


// Chart type → human-readable label (used in "Show Chart Name" badge)
const CHART_TYPE_LABELS: Record<string, string> = {
    bar: 'Bar', line: 'Line', area: 'Area', pie: 'Pie', donut: 'Donut',
    scatter: 'Scatter', bubble: 'Bubble', kpi: 'KPI', table: 'Table',
    combo: 'Combo', heatmap: 'Heatmap', sankey: 'Sankey', funnel: 'Funnel',
    treemap: 'Treemap', gauge: 'Gauge', sparkline: 'Sparkline',
    waterfall: 'Waterfall', boxplot: 'Box Plot', filter: 'Filter',
    text: 'Text', pageHeader: 'Header',
};

class DashboardErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: any, errorInfo: any}> {
    constructor(props: any) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }
    static getDerivedStateFromError(error: any) {
        return { hasError: true };
    }
    componentDidCatch(error: any, errorInfo: any) {
        this.setState({ error, errorInfo });
        console.error("Dashboard Error Boundary Caught:", error, errorInfo);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="p-8 border border-red-200 bg-red-50 text-red-900 m-4 rounded-xl shadow-lg">
                    <h2 className="text-xl font-bold mb-2 flex items-center gap-2"><span>⚠️</span> Dashboard Rendering Error</h2>
                    <p className="mb-4 text-sm font-medium">An unexpected error occurred while rendering the dashboard canvas. The exact error trace is shown below. Please copy it to help debugging.</p>
                    <pre className="text-xs bg-red-900 text-red-100 p-4 rounded overflow-auto max-h-96">
                        {this.state.error && this.state.error.toString()}
                        <br/><br/>
                        {this.state.errorInfo && this.state.errorInfo.componentStack}
                    </pre>
                    <button 
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium"
                    >
                        Reload Page
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

const ResponsiveGridLayout = WidthProvider(Responsive);

// User Defaults (High Resolution Scale - 120 Cols)
// Requirement: Rectangular default.
// In 120 cols: 30 cols = 1/4 screen, 40 cols = 1/3 screen.
// Height: 30 rows * 5px = 150px.
const defaultWidth = 30;
const defaultHeight = 20;

export default function DashboardsPage({
    dashboardId: propDashboardId,
    isNewDashboard: propIsNewDashboard,
    // isEditMode = false, // Usage implies edit/view distinction if needed
    onBack: propOnBack,
    onSaveComplete,
    onNewDashboardSaved,
}: {
    dashboardId?: string;
    isEditMode?: boolean;
    isNewDashboard?: boolean;
    onBack?: () => void;
    onSaveComplete?: () => void;
    /** Called after the very first save of a new dashboard (embedded mode only). Provides the new ID so the parent can update its state without a URL navigation. */
    onNewDashboardSaved?: (newId: string) => void;
} = {}) {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const { addNotification } = useNotification();
    const { user } = useAuth();

    // Prioritize prop ID over params ID
    const dashboardId = propDashboardId || params.id;
    const isNewDashboardSession = propIsNewDashboard != null ? propIsNewDashboard : !dashboardId;

    // Mutable ref that tracks the real dashboard ID even before the parent re-renders
    // with the updated prop (e.g. after first save of a new dashboard).
    const resolvedDashboardIdRef = React.useRef<string | undefined>(dashboardId);
    React.useEffect(() => { resolvedDashboardIdRef.current = dashboardId; }, [dashboardId]);

    // Auto-open version history panel via URL param (?openHistory=1)
    useEffect(() => {
        if (searchParams.get('openHistory') === '1') {
            setVersionHistoryOpen(true);
        }
    }, [searchParams]);

    // ALWAYS close the history panel when switching / opening a dashboard.
    // The custom event (dash:openHistory) fires 150ms later for Version History clicks,
    // so it still opens correctly. Plain Edit clicks never fire that event → panel stays closed.
    useEffect(() => {
        setVersionHistoryOpen(false);
    }, [dashboardId]);

    // Listen for 'dash:openHistory' custom event fired by the landing page Version History button.
    useEffect(() => {
        const handler = (e: Event) => {
            const { id } = (e as CustomEvent).detail || {};
            if (id && id === dashboardId) {
                setVersionHistoryOpen(true);
            }
        };
        window.addEventListener('dash:openHistory', handler);
        return () => window.removeEventListener('dash:openHistory', handler);
    }, [dashboardId]);

    // Handle Back Navigation
    const handleBack = () => {
        console.log('[DashboardsPage] handleBack called. Has propOnBack?', !!propOnBack);
        if (propOnBack) {
            propOnBack();
        } else {
            navigate('/dashboards');
        }
    };

    // 1. Content State
    const [widgetItems, setWidgetItems] = useState<any[]>([]);
    const [dashboardTitle, setDashboardTitle] = useState("New Dashboard");
    // Auto-open the Data popup when creating a brand-new dashboard (no saved ID yet)
    const [initialDataOpen] = useState<boolean>(() =>
        propIsNewDashboard != null ? propIsNewDashboard : !(propDashboardId || params.id)
    );

    // 2. Layout State
    const [layouts, setLayouts] = useState<any>({ lg: [] });
    // Requirement: Allow free placement by default (user request), was 'vertical'
    const [compactType, setCompactType] = useState<'vertical' | 'horizontal' | null>(null);
    const [isCompact, setIsCompact] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    // 3. Immediate Layout Ref
    const layoutRef = React.useRef<any[]>([]);

    // 4. Breakpoint State
    const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');

    // 5. Grid Config (Ultra-High Resolution for +/- 5px resizing)
    // CRITICAL FIX: Keep cols consistent (120) across desktop/laptop to ensure proportionate scaling
    const colsMap = { lg: 120, md: 120, sm: 120, xs: 60, xxs: 40 };

    // 6. Selection State
    const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
    const [selectionTrigger, setSelectionTrigger] = useState(0); // Force-refresh trigger
    const [widgetToDelete, setWidgetToDelete] = useState<string | null>(null);
    const [activeInsightWidgetId, setActiveInsightWidgetId] = useState<string | null>(null); // Track active AI panel
    const [dashboardDescription, setDashboardDescription] = useState("");
    const [logoUrl, setLogoUrl] = useState("");
    const [showTitle, setShowTitle] = useState(true);
    const [showLogo, setShowLogo] = useState(true);
    const [titleStyle, setTitleStyle] = useState<any>({});
    const [canvasStyle, setCanvasStyle] = useState<any>({});
    const [isSaving, setIsSaving] = useState(false);
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const [isHeaderSettingsOpen, setIsHeaderSettingsOpen] = useState(false);
    // Simple name+description settings dialog (like Flow Settings)
    const [isDashSettingsOpen, setIsDashSettingsOpen] = useState(false);
    const [editDashName, setEditDashName] = useState('');
    const [editDashDesc, setEditDashDesc] = useState('');
    const [savingDashSettings, setSavingDashSettings] = useState(false);
    // Dashboard visual theme (decoupled: color palette + widget layout)
    const [dashboardColorTheme, setDashboardColorTheme] = useState<ColorThemeId>('light');
    const [dashboardLayout, setDashboardLayout] = useState<LayoutId>('flat');
    const [dashboardShareable, setDashboardShareable] = useState(false);
    const [editDashShareable, setEditDashShareable] = useState(false);
    const [customMeasures, setCustomMeasures] = useState<any[]>([]);
    const [isMeasureWizardOpen, setIsMeasureWizardOpen] = useState(false);
    const [measureToEdit, setMeasureToEdit] = useState<any>(null);
    const [selectedDataModel, setSelectedDataModel] = useState<any>(null); // active data model from DATA tab
    const [isPreviewModelOpen, setIsPreviewModelOpen] = useState(false);
    const [previewStartTable, setPreviewStartTable] = useState<string>('');
    const [measureToDelete, setMeasureToDelete] = useState<any>(null);
    const [measureUsageAlert, setMeasureUsageAlert] = useState<{ isOpen: boolean, measureName: string }>({ isOpen: false, measureName: '' });
    // Track current version number to pre-fill the next publish modal
    const [dashboardVersionNumber, setDashboardVersionNumber] = useState<number>(0);
    // Active published version label shown in header (e.g. 'v2') — fetched from version history
    const [activePublishedVersion, setActivePublishedVersion] = useState<string | null>(null);

    // Published-state banner (second-layer edit protection for direct URL access)
    const [dashboardStatus, setDashboardStatus] = useState<string>('draft');
    const [isUnpublishingFromBanner, setIsUnpublishingFromBanner] = useState(false);
    const [isShareSheetOpen, setIsShareSheetOpen] = useState(false);
    const [sharedWith, setSharedWith] = useState<string[]>([]);
    const [publishShareMode, setPublishShareMode] = useState<'me' | 'all'>('me');
    const [shareUserCandidates, setShareUserCandidates] = useState<Array<{ id: string; name?: string; email?: string }>>([]);


    // Sync Ref
    useEffect(() => {
        layoutRef.current = layouts[currentBreakpoint] || [];
        // Ensure standard defaults
    }, [layouts, currentBreakpoint]);

    const handleLayoutChange = (currentLayout: any[], allLayouts: any) => {
        if (!currentLayout) return;
        setLayouts(allLayouts);
        layoutRef.current = currentLayout;

        // CRITICAL FIX: Sync layout changes back to widget state to persist resize/move
        setWidgetItems(prevItems => {
            let hasChanged = false;
            const newItems = prevItems.map(item => {
                const layoutItem = currentLayout.find(l => l.i === item.id);
                if (layoutItem && (
                    !item.layout ||
                    item.layout.w !== layoutItem.w ||
                    item.layout.h !== layoutItem.h ||
                    item.layout.x !== layoutItem.x ||
                    item.layout.y !== layoutItem.y
                )) {
                    hasChanged = true;
                    return { ...item, layout: layoutItem };
                }
                return item;
            });
            if (hasChanged) {
                setTimeout(() => setIsDirty(true), 0);
            }
            return hasChanged ? newItems : prevItems;
        });
    };

    const onBreakpointChange = (newBreakpoint: string) => {
        setCurrentBreakpoint(newBreakpoint);
        layoutRef.current = layouts[newBreakpoint] || [];
    };

    const handleCompactToggle = (isCompactVal: boolean) => {
        setIsCompact(isCompactVal);
        setCompactType(isCompactVal ? 'vertical' : null);
        setIsDirty(true);
    };

    // === Save Dashboard Handler ===
    // Returns a Promise that resolves once the actual API call completes
    // (not just when the setTimeout is scheduled).
    const handleSaveDashboard = (status = 'draft', shouldExit = false, overrideName?: string, overrideDesc?: string): Promise<void> => {
        if (!overrideName && dashboardTitle === "New Dashboard" && !dashboardId) {
            // Pre-fill with a timestamped default so user can just click "Confirm & Save"
            // without being forced to type a name. They can still edit it before saving.
            const defaultName = `New Dashboard - ${formatDateTime(new Date())}`;
            setEditDashName(defaultName);
            setEditDashDesc(dashboardDescription || "");
            setEditDashShareable(dashboardShareable);
            setIsDashSettingsOpen(true);
            return Promise.resolve();
        }

        setIsSaving(true);
        // Use a short paint-yield delay so the spinner renders before the
        // heavy html2canvas operation blocks the main thread.
        return new Promise<void>((resolve, reject) => {
            setTimeout(async () => {
                // Capture Snapshot
                let snapshotBase64 = null;
                try {
                    const dashboardElement = document.getElementById('dashboard-canvas');
                    if (dashboardElement) {
                        console.log('Capturing Dashboard Snapshot...');
                        const canvas = await html2canvas(dashboardElement, {
                            scale: 1,
                            useCORS: true,
                            logging: false,
                            allowTaint: true,
                            backgroundColor: '#f8fafc',
                            ignoreElements: (element) =>
                                element.classList.contains('no-snapshot') ||
                                element.classList.contains('react-resizable-handle'),
                            onclone: (clonedDoc) => {
                                const originalCanvases = document.querySelectorAll('#dashboard-canvas canvas');
                                const clonedCanvases = clonedDoc.querySelectorAll('#dashboard-canvas canvas');
                                Array.from(originalCanvases).forEach((original: any, index) => {
                                    const clonedCanvas = clonedCanvases[index];
                                    if (clonedCanvas && original) {
                                        try {
                                            const dataURL = original.toDataURL();
                                            const img = clonedDoc.createElement('img');
                                            img.src = dataURL;
                                            img.style.width = original.style.width || '100%';
                                            img.style.height = original.style.height || '100%';
                                            img.style.display = 'block';
                                            const parent = clonedCanvas.parentElement;
                                            if (parent) parent.replaceChild(img, clonedCanvas);
                                        } catch (e) {
                                            console.warn('Snapshot: Canvas-to-Image replacement failed', e);
                                        }
                                    }
                                });
                            }
                        });
                        if (canvas.width > 0 && canvas.height > 0) {
                            snapshotBase64 = canvas.toDataURL('image/jpeg', 0.8);
                            console.log('Snapshot Captured. Size:', snapshotBase64.length);
                        }
                    }
                } catch (err) {
                    console.warn('Snapshot capture failed:', err);
                }

                try {
                    const payload = {
                        name: overrideName || dashboardTitle,
                        description: overrideDesc !== undefined ? overrideDesc : dashboardDescription,
                        // NOTE: Do NOT include 'status' here.
                        // is managed exclusively by the /publish and /unpublish endpoints.
                        snapshot: snapshotBase64,
                        customMeasures: customMeasures,
                        theme_config: {
                            header: { logoUrl, showTitle, showLogo, titleStyle },
                            canvas: canvasStyle,
                            colorTheme: dashboardColorTheme,
                            layout: dashboardLayout,
                            // Keep legacy 'theme' field so older viewers can still read it
                            theme: dashboardColorTheme,
                            datasetId: selectedDataset?.id,
                            dataModelId: selectedDataModel?.id,
                        },
                        layout_config: widgetItems.map(w => ({
                            i: w.id,
                            x: w.layout?.x || 0,
                            y: w.layout?.y || 0,
                            w: w.layout?.w || defaultWidth,
                            h: w.layout?.h || defaultHeight
                        })),
                        widgets: widgetItems.reduce((acc: any, w) => {
                            const effectiveModelId = w.dataModelId || selectedDataModel?.id || null;
                            acc[w.id] = {
                                id: w.id,
                                title: w.title,
                                type: w.type,
                                data_model_id: effectiveModelId,
                                config: w.config,
                                layout: w.layout
                            };
                            return acc;
                        }, {}),
                        build_version: BUILD_VERSION
                    };

                    // Use the resolved ID if available (e.g. after a first-save in the same session)
                    const currentId = resolvedDashboardIdRef.current || dashboardId;
                    const method = currentId ? 'PUT' : 'POST';
                    const url = currentId ? `/dashboards/${currentId}` : '/dashboards';
                    const savedDashboard = await apiFetch(url, null, { method, body: JSON.stringify(payload) });

                    console.log('Dashboard saved successfully!', savedDashboard);
                    addNotification('Dashboard saved successfully!', 'success');
                    setIsDirty(false);

                    // Immediately update the breadcrumb with the real dashboard name.
                    // GlobalBreadcrumbs listens for this event via `set-breadcrumb-title`.
                    const savedName = overrideName || dashboardTitle;
                    window.dispatchEvent(new CustomEvent('set-breadcrumb-title', { detail: savedName }));

                    // If this was a new dashboard, update the ref + notify parent
                    if (!currentId && savedDashboard?.id) {
                        resolvedDashboardIdRef.current = savedDashboard.id;
                        if (propOnBack) {
                            if (onNewDashboardSaved) onNewDashboardSaved(savedDashboard.id);
                        } else {
                            navigate(`/dashboards/${savedDashboard.id}`, { replace: true });
                        }
                    }

                    if (shouldExit) {
                        if (onSaveComplete) onSaveComplete();
                        else handleBack();
                    }
                    resolve();
                } catch (e: any) {
                    console.error('Save failed:', e);
                    addNotification(e.message || 'Save failed', 'error');
                    reject(e);
                } finally {
                    if (!shouldExit) setIsSaving(false);
                }
            }, 1500);
        });
    };

    const sanitizeFilePart = (value: string) =>
        (value || 'dashboard').replace(/[^a-z0-9]/gi, '_').toLowerCase();

    const exportDashboardAsTemplate = async () => {
        if (!dashboardId) return;
        try {
            const res = await apiFetch(`/dashboards/${dashboardId}/export`, null, { method: 'POST' });
            const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(res, null, 2));
            const a = document.createElement('a');
            a.setAttribute("href", dataStr);
            a.setAttribute("download", `${sanitizeFilePart(dashboardTitle)}_export.json`);
            document.body.appendChild(a);
            a.click();
            a.remove();
            addNotification("Dashboard exported successfully", "success");
        } catch (e: any) {
            console.error('Export failed:', e);
            addNotification(e.message || "Export failed", "error");
        }
    };

    const exportSnapshotPdf = async () => {
        if (isExportingPdf) return;
        const dashboardElement = document.getElementById('dashboard-canvas');
        if (!dashboardElement) throw new Error('Dashboard canvas not found');

        try {
            const EXPORT_TIMEOUT_MS = 30000;
            const POLL_MS = 160;
            // Root cause fix:
            // WidgetRenderer only swaps ECharts canvas -> image when `isSnapshot` is true.
            // Save flow sets this via `isSaving`, but PDF flow previously did not, which left
            // chart canvases blank in html2canvas output on some browsers/GPU paths.
            setIsExportingPdf(true);

            // Let snapshot mode propagate first.
            await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

            // Wait until all chart widgets report snapshot-ready and loaders disappear.
            const startedAt = Date.now();
            while (Date.now() - startedAt < EXPORT_TIMEOUT_MS) {
                const pendingSnapshots = dashboardElement.querySelectorAll('[data-snapshot-ready="false"]').length;
                const loadingSpinners = dashboardElement.querySelectorAll('.animate-spin').length;
                const loadingLabels = dashboardElement.querySelectorAll('[data-widget-loading="true"]').length;
                const pendingApiCalls = Number((window as any).__widgetApiPending || 0);

                if (pendingSnapshots === 0 && loadingSpinners === 0 && loadingLabels === 0 && pendingApiCalls === 0) break;
                await new Promise<void>((resolve) => setTimeout(resolve, POLL_MS));
            }

            const canvas = await html2canvas(dashboardElement, {
                scale: 2,
                useCORS: true,
                logging: false,
                allowTaint: true,
                backgroundColor: '#f8fafc',
                ignoreElements: (element) =>
                    element.classList.contains('no-snapshot') ||
                    element.classList.contains('react-resizable-handle'),
            });

            const imageData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'pt', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
            const drawWidth = canvas.width * ratio;
            const drawHeight = canvas.height * ratio;
            const x = (pageWidth - drawWidth) / 2;
            const y = 24;

            pdf.addImage(imageData, 'PNG', x, y, drawWidth, drawHeight, undefined, 'FAST');
            pdf.save(`${sanitizeFilePart(dashboardTitle)}_snapshot.pdf`);
        } finally {
            setIsExportingPdf(false);
        }
    };

    const applyShareSettings = async ({
        audience,
        subjectIds,
        expiresAt,
    }: {
        audience: 'organization' | 'existing_access' | 'specific_people' | 'tenant_admins';
        subjectIds: string[];
        expiresAt: string | null;
    }) => {
        const id = resolvedDashboardIdRef.current || dashboardId;
        if (!id) throw new Error('Save the dashboard before configuring share settings.');

        const res = await apiFetch(`/dashboards/${id}/share-link`, null, {
            method: 'POST',
            body: JSON.stringify({
                audience,
                subjectIds,
                expiresAt,
                publishShareMode,
            }),
        });

        const nextSharedWith = Array.isArray(res?.sharedWith) ? res.sharedWith : [];
        setSharedWith(nextSharedWith);

        const relativeUrl = res?.relativeUrl;
        if (!relativeUrl || !String(relativeUrl).includes('st=') || !String(relativeUrl).includes('kv=')) {
            throw new Error('Failed to generate secure share link. Please try again.');
        }
        const finalLink = `${window.location.origin}${relativeUrl}`;
        addNotification('Link settings updated', 'success');
        return finalLink;
    };

    useEffect(() => {
        const loadShareCandidates = async () => {
            if (!isShareSheetOpen) return;
            if (!(user?.roleId && ['r1', 'r2', 'r3', 'r4', 'r7'].includes(user.roleId))) {
                setShareUserCandidates([]);
                return;
            }
            try {
                const endpoint = user?.tenantId ? `/users?tenant_id=${user.tenantId}` : '/users';
                const users = await apiFetch(endpoint, null, { method: 'GET' });
                const list = Array.isArray(users) ? users : [];
                const candidates = list
                    .filter((u: any) => String(u?.status || '').toLowerCase() === 'active')
                    .map((u: any) => ({ id: String(u.id), name: u.name, email: u.email }));
                setShareUserCandidates(candidates);
            } catch {
                setShareUserCandidates([]);
            }
        };
        loadShareCandidates();
    }, [isShareSheetOpen, user?.roleId, user?.tenantId]);

    // === Publish Handler ===
    const [publishModalOpen, setPublishModalOpen] = useState(false);
    const [publishError, setPublishError] = useState<string | null>(null);
    const [nextVersionLabel, setNextVersionLabel] = useState<string>('v1');

    // === Version History Panel ===
    const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);

    // Called from DashboardVersionHistoryPanel when user clicks Load Snapshot
    const handleLoadSnapshot = (snapshot: any) => {
        if (!snapshot) return;
        // Restore layout
        if (snapshot.layout_config) {
            setLayouts({ lg: snapshot.layout_config });
        }
        // Restore widgets
        if (snapshot.widgets) {
            const widgetItems = Object.entries(snapshot.widgets).map(([id, w]: [string, any]) => {
                const effectiveModelId = w.data_model_id || w.dataModelId || w.config?.dataModelId;
                return {
                    id,
                    type: w.type || 'bar',
                    title: w.title,
                    dataModelId: effectiveModelId,
                    dashboardId: dashboardId, // CRITICAL: needed by FilterWidget for dashboard_id in queries
                    config: w.config || {},
                    custom_measures: w.custom_measures,
                    layout: (snapshot.layout_config || []).find((l: any) => l.i === id),
                };
            });
            setWidgetItems(widgetItems);

            // Re-initialize default filters from the snapshot's filter widgets
            const initialFilters: Record<string, any> = {};
            widgetItems.forEach(w => {
                if (w.type === 'filter' && w.config?.column) {
                    w.config.column.forEach((col: any) => {
                        if (col.defaultValue !== undefined && col.defaultValue !== null && col.defaultValue !== '') {
                            initialFilters[col.name] = col.defaultValue;
                        }
                    });
                }
            });
            if (Object.keys(initialFilters).length > 0) {
                setGlobalFilters(initialFilters);
            }
        }
        // Restore theme
        if (snapshot.theme_config) {
            const tc = snapshot.theme_config;
            if (tc.header) {
                setLogoUrl(tc.header.logoUrl || '');
                setShowTitle(tc.header.showTitle !== false);
                setShowLogo(tc.header.showLogo !== false);
                setTitleStyle(tc.header.titleStyle || {});
            }
            if (tc.canvas) setCanvasStyle(tc.canvas);
            // New fields first, fall back to legacy 'theme'
            setDashboardColorTheme(resolveColorTheme(tc.colorTheme || tc.theme || 'light').id as ColorThemeId);
            setDashboardLayout((tc.layout || 'flat') as LayoutId);
        }
        // Restore measures
        if (snapshot.custom_measures) {
            setCustomMeasures(snapshot.custom_measures);
        }

        // Restore Data Context (Dataset and DataModel)
        let snapshotDataModelId = null;
        let snapshotDatasetId = null;

        if (snapshot.theme_config) {
            snapshotDatasetId = snapshot.theme_config.datasetId;
            snapshotDataModelId = snapshot.theme_config.dataModelId;
        }

        if (!snapshotDataModelId && snapshot.widgets) {
            snapshotDataModelId = Object.values(snapshot.widgets).map((c: any) => c.data_model_id || c.dataModelId).find(Boolean);
        }
        
        if (!snapshotDatasetId && snapshot.widgets) {
            snapshotDatasetId = Object.values(snapshot.widgets).map((c: any) => c.dataset_id || c.datasetId).find(Boolean);
        }

        if (snapshotDatasetId && rawDatasets) {
            const savedDataset = rawDatasets.find((d: any) => d.id === snapshotDatasetId);
            if (savedDataset) {
                setSelectedDataset(savedDataset);
            }
        }

        if (snapshotDataModelId) {
            apiFetch(`/datamodels/${snapshotDataModelId}`)
                .then((dm: any) => setSelectedDataModel(dm))
                .catch(() => { });
        }
        setIsDirty(true);
    };

    const openPublishModal = async () => {
        setPublishError(null);

        // For a brand-new dashboard that hasn't been saved yet,
        // auto-save first so the dashboard has an ID before the modal opens.
        // This prevents "publish undefined" and avoids duplicate dashboard rows.
        const currentId = resolvedDashboardIdRef.current || dashboardId;
        if (!currentId) {
            try {
                await handleSaveDashboard('draft', false);
            } catch {
                addNotification('Failed to save dashboard before publishing. Please save manually.', 'error');
                return;
            }
        }

        // Fetch live version list to compute accurate next version label
        const idForVersions = resolvedDashboardIdRef.current || dashboardId;
        if (idForVersions) {
            try {
                const versions = await apiFetch(`/dashboards/${idForVersions}/versions`);
                const maxVer = Array.isArray(versions) && versions.length > 0
                    ? Math.max(...versions.map((v: any) => v.version_number || 0))
                    : 0;
                setNextVersionLabel(`v${maxVer + 1}`);
            } catch {
                setNextVersionLabel(`v${dashboardVersionNumber + 1}`);
            }
        } else {
            setNextVersionLabel('v1');
        }
        setPublishModalOpen(true);
    };

    const handleUnpublishFromBanner = async () => {
        const id = resolvedDashboardIdRef.current || dashboardId;
        if (!id) return;
        setIsUnpublishingFromBanner(true);
        try {
            await apiFetch(`/dashboards/${id}/unpublish`, null, { method: 'POST' });
            setDashboardStatus('draft');
            addNotification(`"${dashboardTitle}" unlocked for editing — version history preserved`, 'success');
            window.dispatchEvent(new Event('refresh-sidebar'));
        } catch (e: any) {
            addNotification(e.message || 'Failed to unpublish', 'error');
        } finally {
            setIsUnpublishingFromBanner(false);
        }
    };

    // handleConfirmPublish: the dashboard ALREADY has an ID at this point
    // because openPublishModal auto-saved it if needed.
    const handleConfirmPublish = async (
        name: string,
        description: string,
        contentTypeId: number,
        sharedWith: string[],
        folderName: string,
        status: string,
        _overwrite: boolean,
        _linkedDashId: string,
        versionLabel: string
    ) => {
        setPublishError(null);

        const id = resolvedDashboardIdRef.current || dashboardId;
        if (!id) {
            addNotification('Dashboard not saved yet. Please save before publishing.', 'warning');
            return;
        }

        try {
            await apiFetch(`/dashboards/${id}/publish`, null, {
                method: 'POST',
                body: JSON.stringify({
                    type: 'dashboard',
                    name: name || dashboardTitle,
                    description: description || dashboardDescription,
                    contentTypeId,
                    sharedWith,
                    folderName,
                    status: status || 'Published',
                    versionLabel: versionLabel || undefined,
                }),
            });
            setSharedWith(sharedWith);
            setPublishShareMode(sharedWith.includes('*') ? 'all' : 'me');

            addNotification('Dashboard published successfully!', 'success');
            setIsDirty(false);
            if (versionLabel) setActivePublishedVersion(versionLabel);
            setPublishModalOpen(false);
            window.dispatchEvent(new Event('refresh-sidebar'));
            // Refresh active version badge in header
            try {
                const versions = await apiFetch(`/dashboards/${id}/versions`);
                const activeVer = Array.isArray(versions) && versions.length > 0
                    ? (versions.find((v: any) => v.is_active) || versions.reduce((a: any, b: any) => b.version_number > a.version_number ? b : a))
                    : null;
                if (activeVer) setActivePublishedVersion(activeVer.version || `v${activeVer.version_number}`);
            } catch { /* non-critical */ }
            window.dispatchEvent(new CustomEvent('dash:published', { detail: { id } }));
            if (onSaveComplete) {
                setTimeout(() => onSaveComplete!(), 600);
            } else {
                setTimeout(() => handleBack(), 600);
            }
        } catch (e: any) {
            const msg = e.message || 'Publish failed';
            if (msg.includes('already exists')) {
                setPublishError(msg);
            } else {
                addNotification(msg, 'error');
            }
            throw e;
        }
    };

    const handleHeaderSettingsSave = (settings: any) => {
        setDashboardTitle(settings.title);
        setDashboardDescription(settings.description);
        setLogoUrl(settings.logoUrl);
        setShowTitle(settings.showTitle);
        setShowLogo(settings.showLogo);
        setTitleStyle(settings.titleStyle || {});
        setCanvasStyle(settings.canvas || {});
    };

    const saveDashboardSettings = async () => {
        setSavingDashSettings(true);
        try {
            const finalName = editDashName.trim() || dashboardTitle;
            const finalDesc = editDashDesc.trim();
            if (dashboardId) {
                await apiFetch(`/dashboards/${dashboardId}`, null, {
                    method: 'PUT',
                    body: JSON.stringify({ name: finalName, description: finalDesc }),
                });
            }
            setDashboardTitle(finalName);
            setDashboardDescription(finalDesc);
            setDashboardShareable(editDashShareable);
            setIsDashSettingsOpen(false);
            
            if (!dashboardId) {
                await handleSaveDashboard('draft', false, finalName, finalDesc);
            } else {
                addNotification('Dashboard settings saved — click Save to persist the theme.', 'success');
            }
            // Sync breadcrumb immediately after a name change.
            window.dispatchEvent(new CustomEvent('set-breadcrumb-title', { detail: finalName }));
        } catch {
            addNotification('Failed to save settings', 'error');
        } finally {
            setSavingDashSettings(false);
        }
    };

    // Lightweight helper: persist ONLY customMeasures to the backend (no screenshot).
    // Called immediately when a measure is created, edited, or deleted.
    const saveMeasuresOnly = async (measures: any[]) => {
        if (!dashboardId) return; // Nothing to update if dashboard hasn't been saved yet
        try {
            await apiFetch(`/dashboards/${dashboardId}`, null, {
                method: 'PUT',
                body: JSON.stringify({ customMeasures: measures })
            });
            console.log('[Measures] Auto-saved', measures.length, 'measures to backend.');
        } catch (err) {
            console.error('[Measures] Auto-save failed:', err);
            addNotification('Measure saved locally. Press Save to persist.', 'warning');
        }
    };

    // Helper: Find first empty slot (Calendar Order: Left->Right, then Top->Bottom)
    const findNextAvailablePosition = (layoutItems: any[], w: number, h: number, cols: number) => {
        if (!layoutItems || layoutItems.length === 0) return { x: 0, y: 0 };

        // Scan explicitly in calendar order (Strict: checking every row)
        for (let y = 0; y < 1000; y++) {
            // x step = 1
            for (let x = 0; x <= (cols - w); x++) {
                const collision = layoutItems.some(item => {
                    return (
                        x < item.x + item.w &&
                        x + w > item.x &&
                        y < item.y + item.h &&
                        y + h > item.y
                    );
                });
                if (!collision) return { x, y };
            }
        }
        // Fallback: below everything
        const maxY = layoutItems.length > 0
            ? Math.max(...layoutItems.map(item => item.y + item.h))
            : 0;
        return { x: 0, y: maxY };
    };

    // Add Widget Logic
    const addWidget = (type: string, x: any = null, y: any = null, config = {}, title: string | null = null) => {
        const id = `widget-${Date.now()}`;
        const totalCols = (colsMap as any)[currentBreakpoint] || 120;

        let w = defaultWidth;
        if (w > totalCols) w = totalCols;
        const h = defaultHeight;

        let finalX = x;
        let finalY = y;

        // Auto-placement Strategy
        if (finalX === null || finalY === null || finalY === undefined) {
            // If x is an object (config passed from Toolbox click), treat as null position
            if (typeof x === 'object' && x !== null) {
                finalX = null;
                finalY = null;
            } else {
                const pos = findNextAvailablePosition(layoutRef.current, w, h, totalCols);
                finalX = pos.x;
                finalY = pos.y;
            }
        }

        // Re-check auto-placement if we reset above
        if (finalX === null) {
            const pos = findNextAvailablePosition(layoutRef.current, w, h, totalCols);
            finalX = pos.x;
            finalY = pos.y;
        }

        const newLayoutItem = { i: id, x: finalX, y: finalY, w, h };

        // CRITICAL FIX: explicit layout in widget state prevents default 0,0 fallback collision
        const newWidget = {
            id,
            type,
            title: title || `${type.charAt(0).toUpperCase() + type.slice(1)} Chart`,
            datasetId: selectedDataset?.id, // CRITICAL: Link to dataset
            dataModelId: selectedDataModel?.id || null, // Stamp data model so queries fire immediately without save
            dashboardId: dashboardId || null,            // Stamp dashboard ID for query context
            config: { ...(config || {}) }, // Merge preset config
            layout: newLayoutItem
        };

        setWidgetItems(prev => [...prev, newWidget]);
        setIsDirty(true);

        // Auto-select new widget
        setSelectedWidgetId(id);
        setSelectionTrigger(prev => prev + 1);

        // Update Ref & State
        layoutRef.current = [...layoutRef.current, newLayoutItem];
        setLayouts((prev: any) => ({
            ...prev,
            [currentBreakpoint]: layoutRef.current
        }));
    };

    const removeWidget = () => {
        if (!widgetToDelete) return;
        setWidgetItems(prev => prev.filter(w => w.id !== widgetToDelete));
        setIsDirty(true);
        setLayouts((prev: any) => ({
            ...prev,
            [currentBreakpoint]: (prev[currentBreakpoint] || []).filter((item: any) => item.i !== widgetToDelete)
        }));
        if (selectedWidgetId === widgetToDelete) setSelectedWidgetId(null);
        setWidgetToDelete(null);
        addNotification("Widget deleted", "success");
    };

    const updateWidget = (id: string, updates: any) => {
        setWidgetItems(prev => prev.map(w => w.id === id ? { ...w, ...updates } : w));
        setIsDirty(true);
    };

    // Apply a dashboard template — replaces current canvas with template widgets
    const handleTemplateApply = (template: any) => {
        if (!template?.widgets?.length) return;

        // Switch to Free Layout to preserve grid alignment
        setIsCompact(false);
        setCompactType(null);
        setIsDirty(true);

        const breakpoints = ['lg', 'md', 'sm', 'xs', 'xxs'];
        const colsMapScaled = { lg: 120, md: 96, sm: 72, xs: 48, xxs: 24 };
        
        const newLayouts: Record<string, any[]> = {};
        breakpoints.forEach(bp => {
            newLayouts[bp] = [];
        });

        const newWidgets: any[] = [];

        template.widgets.forEach((tw: any) => {
            const id = `widget-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
            
            breakpoints.forEach(bp => {
                const scale = colsMapScaled[bp as keyof typeof colsMapScaled] / 120;
                newLayouts[bp].push({
                    i: id,
                    x: Math.round(tw.layout.x * scale),
                    y: tw.layout.y,
                    w: Math.max(1, Math.round(tw.layout.w * scale)),
                    h: tw.layout.h
                });
            });

            const defaultLayoutItem = newLayouts['lg'].find(item => item.i === id);
            const config = tw.type === 'pageHeader' ? { title: template.name } : (tw.config || {});
            
            newWidgets.push({
                id,
                type: tw.type,
                title: tw.title,
                datasetId: selectedDataset?.id,
                dataModelId: selectedDataModel?.id || null,
                dashboardId: dashboardId || null,
                config: config,
                layout: defaultLayoutItem,
            });
        });

        setWidgetItems(newWidgets);
        layoutRef.current = newLayouts[currentBreakpoint] || newLayouts['lg'];
        setLayouts(newLayouts);
        setSelectedWidgetId(null);
        if (isNewDashboardSession) {
            setDashboardTitle(template.name);
        }
        addNotification(`Template "${template.name}" applied — ${newWidgets.length} widgets added`, 'success');
    };

    const confirmDelete = (e: any, id: string) => {
        e.stopPropagation();
        setWidgetToDelete(id);
    };

    const duplicateWidget = (e: any, id: string) => {
        e.stopPropagation();
        const widgetToCopy = widgetItems.find(w => w.id === id);
        if (!widgetToCopy) return;

        const newId = `widget_${Date.now()}`;
        const layoutItemToCopy = layoutRef.current.find(l => l.i === id);
        
        // Place new widget below the original
        const newLayoutItem = layoutItemToCopy ? {
            ...layoutItemToCopy,
            i: newId,
            y: layoutItemToCopy.y + layoutItemToCopy.h
        } : {
            i: newId,
            x: 0, y: Infinity, w: 6, h: 4
        };

        const newWidget = {
            ...widgetToCopy,
            id: newId,
            layout: newLayoutItem,
            config: JSON.parse(JSON.stringify(widgetToCopy.config || {}))
        };

        setWidgetItems(prev => [...prev, newWidget]);
        setIsDirty(true);
        
        const newLayouts = [...layoutRef.current, newLayoutItem];
        layoutRef.current = newLayouts;
        setLayouts(prev => {
            const upd: any = {};
            for (const key in prev) {
                upd[key] = [...prev[key], newLayoutItem];
            }
            return upd;
        });

        addNotification("Widget duplicated", "success");
    };

    const handleAIInsights = (id: string) => {
        setActiveInsightWidgetId(prev => prev === id ? null : id);
    };

    const onWrapperDrop = (e: any) => {
        e.preventDefault();

        let type = e.dataTransfer.getData("text/plain");
        let config = {};
        let label = null;

        try {
            const json = e.dataTransfer.getData("application/json");
            if (json) {
                const data = JSON.parse(json);
                type = data.type;
                config = data.config || {};
                label = data.label || null;
            }
        } catch (err) { }

        if (!type) return;

        const container = e.currentTarget;
        const rect = container.getBoundingClientRect();
        const scrollTop = container.scrollTop;

        // High Resolution Calc
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top + scrollTop;

        const totalCols = (colsMap as any)[currentBreakpoint] || 120;
        const contentWidth = container.offsetWidth - 64; // Padding adjustment
        const colWidth = contentWidth / totalCols;

        const rowHeight = 5 + 5; // rowHeight + margin

        let gridX = Math.floor((x - 32) / colWidth);
        let gridY = Math.floor((y - 32) / rowHeight);

        // Clamp
        gridX = Math.max(0, Math.min(totalCols - defaultWidth, gridX)); // Ensure it fits in width
        gridY = Math.max(0, gridY);

        // addWidget(type, gridX, gridY, config, label);
        addWidget(type, gridX, gridY, config, label || null);
    };

    // Selection Handling
    const handleWidgetClick = (e: any, id: string) => {
        // e.stopPropagation(); 
        setSelectedWidgetId(id);
        setSelectionTrigger(prev => prev + 1);
    };

    const handleBackgroundClick = (e: any) => {
        // Only deselect if clicking directly on the grid container or wrapper
        // Relaxed check: As long as it's not a widget drag/resize, deselect
        if (!e.target.closest('.react-grid-item')) {
            setSelectedWidgetId(null);
            setSelectionTrigger(prev => prev + 1);
        }
    };

    // -----------------------------------------------------------------------------
    // 6. Global Filter State
    // -----------------------------------------------------------------------------
    const [globalFilters, setGlobalFilters] = useState({}); // { fieldName: value, ... }

    const handleFilterChange = (field: string, value: any) => {
        setGlobalFilters(prev => {
            const next = { ...prev };
            if (value === null || value === undefined || value === '') {
                delete (next as any)[field];
            } else {
                (next as any)[field] = value;
            }
            return next;
        });
    };
    const isTenantContext = window.location.pathname.startsWith('/tenant');
    // Datasets are retired. Hardcode to empty array to prevent 410 Gone errors.
    const rawDatasets: any[] = [];
    const isDatasetsLoading = false;

    // Initial Mapping (Metadata only) - FIX: Infer Schema from Config locally!
    const initialDatasets = useMemo(() => {
        if (!rawDatasets) return [];
        return rawDatasets.map((ds: any) => {
            // Infer from query_config if available to avoid API call
            let localDims = [];
            let localMsrs = [];

            if (ds.query_config?.selected_columns) {
                ds.query_config.selected_columns.forEach((col: any) => {
                    // Heuristic: If it has aggregation, it's likely treated as a measure in the source
                    // But strictly, these are just columns. 
                    // Let's guess based on name for now, similar to the preview logic but without data.
                    const name = col.alias || col.field;
                    const isId = name.toLowerCase().endsWith('id') || name.toLowerCase().endsWith('code');
                    const isAmount = /amount|price|cost|value|revenue|profit|sales|qty|count|score/.test(name.toLowerCase());

                    if (isAmount && !isId) {
                        localMsrs.push({ name, type: 'number', originalType: 'UNKNOWN', tableName: 'Dataset' });
                    } else {
                        // Default to dimension
                        localDims.push({ name, type: 'string', originalType: 'UNKNOWN', tableName: 'Dataset' });
                    }
                });
            }

            return {
                ...ds,
                dimensions: localDims,
                measures: localMsrs
            };
        });
    }, [rawDatasets]);

    const [selectedDataset, setSelectedDataset] = useState<any>(null);
    // "Enriched" dataset contains the inferred schema from preview
    const [enrichedDataset, setEnrichedDataset] = useState<any>(null);

    // Auto-select first is REMOVED/DELAYED: We only auto-select new if nothing selected.
    useEffect(() => {
        if (!selectedDataset && initialDatasets.length > 0 && !dashboardId) {
            // Only auto-select first for BRAND NEW dashboards not loaded ones
            setSelectedDataset(initialDatasets[0]);
        }
    }, [initialDatasets, dashboardId]); // Add dashboardId check




    // Schema Inference Logic - OPTIMIZED: Fetch Columns Only
    useEffect(() => {
        const fetchSchema = async () => {
            if (!selectedDataset) {
                setEnrichedDataset(null);
                return;
            }

            try {
                // Determine dimensions/measures from existing config FIRST (Fastest)
                if (selectedDataset.measures && selectedDataset.measures.length > 0) {
                    setEnrichedDataset({
                        ...selectedDataset,
                        measures: [...selectedDataset.measures, ...customMeasures] // Merge Custom
                    });
                    return;
                }

                // Datasets are retired — rawDatasets is always []; this path is unreachable.
                return;
            } catch (err) {
                console.error("Failed to fetch dataset columns:", err);
                setEnrichedDataset({
                    ...selectedDataset,
                    measures: [...(selectedDataset.measures || []), ...customMeasures]
                });
            }
        };

        fetchSchema();
    }, [selectedDataset?.id, customMeasures]); // Re-run when dataset or measures change

    // No longer declaring 'params' here directly, usage moved to 'dashboardId' derivation

    // === Load Dashboard on Mount ===
    useEffect(() => {
        if (dashboardId) {
            const loadDashboard = async () => {
                try {
                    const dashboard = await apiFetch(`/dashboards/${dashboardId}?mode=edit`);
                    if (dashboard) {
                        const dashName = dashboard.name || 'Untitled';
                        setDashboardTitle(dashName);
                        window.dispatchEvent(new CustomEvent('set-breadcrumb-title', { detail: dashName }));
                        setDashboardDescription(dashboard.description || "");
                        // Track version number for pre-filling publish modal
                        setDashboardVersionNumber(dashboard.version_number || 0);
                        // Show active version badge — use dashboard.version_number as immediate fallback
                        // (set on every publish), while /versions gives us the precise label
                        const vn = dashboard.version_number || 0;
                        if (vn > 0) {
                            // Set immediately so badge appears without waiting for /versions
                            setActivePublishedVersion(`v${vn}`);
                        }
                        // Refine with exact label from version history (e.g. custom labels like 'Sprint-3')
                        try {
                            const versions = await apiFetch(`/dashboards/${dashboardId}/versions`);
                            if (Array.isArray(versions) && versions.length > 0) {
                                const activeVer = versions.find((v: any) => v.is_active)
                                    || versions.reduce((a: any, b: any) => (b.version_number > a.version_number ? b : a));
                                if (activeVer) setActivePublishedVersion(activeVer.version || `v${activeVer.version_number}`);
                            } else if (vn === 0) {
                                setActivePublishedVersion(null);
                            }
                        } catch { /* non-critical — badge already set from version_number above */ }
                        // Second-layer protection: banner if opened directly for a published dashboard
                        const status = (dashboard.status || '').toLowerCase();
                        setDashboardStatus(status);
                        try {
                            const rawShared = dashboard.shared_with ?? dashboard.sharedWith ?? [];
                            const parsed = typeof rawShared === 'string' ? JSON.parse(rawShared) : rawShared;
                            const nextShared = Array.isArray(parsed) ? parsed.map((v: any) => String(v)) : [];
                            setSharedWith(nextShared);
                            setPublishShareMode(nextShared.includes('*') ? 'all' : 'me');
                        } catch {
                            setSharedWith([]);
                            setPublishShareMode('me');
                        }
                        // No banner, no modal — published dashboards open cleanly in view-only mode

                        // LOAD CONFIG (Theme Config)
                        const loadedConfig = dashboard.theme_config || dashboard.config || {};

                        if (loadedConfig.header) {
                            setLogoUrl(loadedConfig.header.logoUrl || "");
                            setShowTitle(loadedConfig.header.showTitle !== false);
                            setShowLogo(loadedConfig.header.showLogo !== false);
                            setTitleStyle(loadedConfig.header.titleStyle || {});
                        }
                        if (loadedConfig.canvas) {
                            setCanvasStyle(loadedConfig.canvas || {});
                        }
                        // New fields first, fall back to legacy 'theme' for migrating old dashboards
                        setDashboardColorTheme(resolveColorTheme(loadedConfig.colorTheme || loadedConfig.theme || 'light').id as ColorThemeId);
                        setDashboardLayout((loadedConfig.layout || 'flat') as LayoutId);

                        // FIX: Read Custom Measures from Root (Backend Mode) or Config (Legacy)
                        // Backend returns snake_case keys from SQLAlchemy dict, so check both
                        const loadedMeasures = dashboard.customMeasures || dashboard.custom_measures || loadedConfig.customMeasures;
                        if (loadedMeasures && loadedMeasures.length > 0) {
                            setCustomMeasures(loadedMeasures);
                        }

                        // Load Dataset from Config
                        if (loadedConfig.datasetId && rawDatasets) {
                            const savedDataset = rawDatasets.find((d: any) => d.id === loadedConfig.datasetId);
                            if (savedDataset) {
                                setSelectedDataset(savedDataset);
                            }
                        }

                        // Restore saved data model from theme_config
                        // If not in theme_config, auto-resolve from the first widget with a data_model_id
                        const themeDataModelId = loadedConfig.dataModelId;
                        const widgetsMap = dashboard.widgets || {};
                        const firstWidgetDataModelId = themeDataModelId ||
                            Object.values(widgetsMap).map((c: any) => c.data_model_id || c.dataModelId).find(Boolean);
                        if (firstWidgetDataModelId) {
                            apiFetch(`/datamodels/${firstWidgetDataModelId}`)
                                .then((dm: any) => setSelectedDataModel(dm))
                                .catch(() => { });
                        }

                        if (dashboard.widgets && dashboard.layout_config) {
                            const loadedWidgets = Object.entries(dashboard.widgets).map(([id, config]: [string, any]) => {
                                return {
                                    id,
                                    type: config.type || config.widgetType || 'bar',
                                    title: config.title,
                                    datasetId: config.dataset_id || config.datasetId || config.config?.datasetId, // CRITICAL FIX B: Load ID
                                    dataModelId: config.data_model_id || config.dataModelId || config.config?.dataModelId, // FIX: Load dataModelId
                                    dashboardId: dashboardId, // CRITICAL: needed by FilterWidget to pass dashboard_id for sibling lookup
                                    config: config.config || {},
                                    layout: config.layout || dashboard.layout_config.find((l: any) => l.i === id)
                                };
                            });
                            setWidgetItems(loadedWidgets);
                            setLayouts({ lg: dashboard.layout_config });

                            // --- INITIALIZE DEFAULT FILTERS ---
                            const initialFilters: Record<string, any> = {};
                            loadedWidgets.forEach(w => {
                                if (w.type === 'filter' && w.config?.column) {
                                    w.config.column.forEach((col: any) => {
                                        if (col.defaultValue !== undefined && col.defaultValue !== null && col.defaultValue !== '') {
                                            initialFilters[col.name] = col.defaultValue;
                                        }
                                    });
                                }
                            });
                            if (Object.keys(initialFilters).length > 0) {
                                console.log('[DashboardsPage] Initializing Default Filters:', initialFilters);
                                setGlobalFilters(initialFilters);
                            }
                        }
                    }
                } catch (e) {
                    console.error('Failed to load dashboard:', e);
                }
            };
            // Load immediately — rawDatasets is best-effort for dataset auto-select
            loadDashboard();
        }
    }, [dashboardId]); // rawDatasets dependency removed — dataset matching is best-effort


    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    // 8. Dataset/DataModel Locking Logic
    const isDataModelLocked = useMemo(() => {
        return widgetItems.some(w =>
            (w.config?.xAxis?.length > 0) ||
            (w.config?.yAxis?.length > 0) ||
            (w.config?.series?.length > 0) ||
            (w.config?.column?.length > 0) || // Filter widgets
            (w.config?.legend?.length > 0)
        );
    }, [widgetItems]);

    // CRITICAL FIX: Trigger manual window resize event to force WidthProvider to re-measure
    useEffect(() => {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 300); // Matches the transition duration (300ms)
    }, [isSidebarOpen]);

    // -------------------------------------------------------------
    // Full Screen Toggle Logic
    // -------------------------------------------------------------
    const [isFullScreen, setIsFullScreen] = useState(false);
    const toggleFullScreen = () => setIsFullScreen(!isFullScreen);

    // Auto-hide controls logic
    const [showControls, setShowControls] = useState(true);
    const controlsTimeoutRef = React.useRef<any>(null);

    const isViewOnly = dashboardStatus === 'published';

    const handleMouseMove = () => {
        setShowControls(true);
        if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
        controlsTimeoutRef.current = setTimeout(() => {
            setShowControls(false);
        }, 3000); // Disappear after 3 seconds
    };

    // Cleanup timeout
    useEffect(() => {
        return () => {
            if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
        };
    }, []);

    // DEBUG: Loading State check
    if (isDatasetsLoading && !rawDatasets) {
        return <div className="flex h-full w-full items-center justify-center text-slate-400">Loading Dashboards...</div>;
    }

    return (
        <DashboardErrorBoundary>
        <div
            className={`flex flex-col bg-[#f0f0f0] dark:bg-slate-900 overflow-hidden text-[#252423] dark:text-slate-100 transition-all duration-300 ${isFullScreen ? 'fixed inset-0 z-50 h-screen w-screen' : 'h-full w-full relative'}`}
            style={{ fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}
            onMouseMove={handleMouseMove}
        >
            {/* HEADER */}
            <div className="shrink-0 z-30 relative">
                <DashboardHeader
                    title={dashboardTitle}
                    onTitleChange={(newTitle: string) => {
                        if (newTitle !== dashboardTitle) {
                            setDashboardTitle(newTitle);
                            setIsDirty(true);
                        }
                    }}
                    onToggleCompact={handleCompactToggle}
                    onAction={async (action) => {
                        if (action === 'fullscreen') toggleFullScreen();
                        else if (action === 'versionHistory') setVersionHistoryOpen(true);
                        else if (action === 'newMeasure') {
                            setMeasureToEdit(null);
                            setIsMeasureWizardOpen(true);
                        }
                        else if (action === 'export-json') {
                            await exportDashboardAsTemplate();
                        }
                        else if (action === 'export-pdf-snapshot') {
                            try {
                                await exportSnapshotPdf();
                                addNotification('Snapshot PDF exported successfully', 'success');
                            } catch (e: any) {
                                addNotification(e.message || 'Snapshot PDF export failed', 'error');
                            }
                        }
                        else if (action === 'share-url') {
                            setIsShareSheetOpen(true);
                        }
                        else console.log("Header Action:", action);
                    }}
                    // Dataset Props
                    selectedDataset={enrichedDataset || selectedDataset || selectedDataModel}
                    onPreviewData={() => {
                        if (selectedDataModel) {
                            setPreviewStartTable('');
                            setIsPreviewModelOpen(true);
                        } else {
                            setIsPreviewOpen(true);
                        }
                    }}
                    isFullScreen={isFullScreen}
                    isSidebarOpen={isSidebarOpen}
                    onToggleSidebar={setIsSidebarOpen}
                    onSave={() => handleSaveDashboard('draft', false)}
                    onPublish={() => openPublishModal()}
                    onHeaderSettings={() => {
                        setEditDashName(dashboardTitle || '');
                        setEditDashDesc(dashboardDescription || '');
                        setEditDashShareable(dashboardShareable);
                        setIsDashSettingsOpen(true);
                    }}
                    onBack={handleBack}
                    isSaving={isSaving}
                    titleStyle={titleStyle}
                    activeVersion={activePublishedVersion}
                    dataModel={selectedDataModel}
                    isDataModelLocked={isDataModelLocked}
                    onDataModelChange={(dm: any) => {
                        setSelectedDataModel(dm);
                        setIsDirty(true);
                    }}
                    onPreviewTable={(tableName: string) => {
                        setPreviewStartTable(tableName);
                        setIsPreviewModelOpen(true);
                    }}
                    initialDataOpen={initialDataOpen}
                    onTemplateApply={handleTemplateApply}
                    widgetCount={widgetItems.length}
                    isNewDashboard={isNewDashboardSession}
                    currentColorTheme={dashboardColorTheme}
                    currentLayout={dashboardLayout}
                    onColorThemeChange={(id: ColorThemeId) => {
                        if (id !== dashboardColorTheme) {
                            setDashboardColorTheme(id);
                            setIsDirty(true);
                        }
                    }}
                    onLayoutChange={(id: LayoutId) => {
                        if (id !== dashboardLayout) {
                            setDashboardLayout(id);
                            setIsDirty(true);
                        }
                    }}
                    isViewOnly={false}
                    isCompact={isCompact}
                    isDirty={isDirty}
                />
            </div>
            <DashboardShareSheet
                open={isShareSheetOpen}
                dashboardId={dashboardId}
                dashboardTitle={dashboardTitle}
                isPublished={dashboardStatus === 'published'}
                canShareWithAll={!!(user?.roleId && ['r1', 'r2', 'r3', 'r4', 'r7'].includes(user.roleId))}
                canShareWithOrg={!!(user?.roleId && ['r1', 'r2', 'r3', 'r4'].includes(user.roleId))}
                sharedWith={sharedWith}
                publishShareMode={publishShareMode}
                availableUsers={shareUserCandidates}
                onClose={() => setIsShareSheetOpen(false)}
                onApplySettings={applyShareSettings}
                onCopyLink={async (link) => {
                    try {
                        await navigator.clipboard.writeText(link);
                        addNotification('Share link copied to clipboard', 'success');
                    } catch {
                        addNotification('Failed to copy link', 'error');
                    }
                }}
            />

            <PublishAppModal
                open={publishModalOpen}
                onOpenChange={(open) => { if (!open) { setPublishModalOpen(false); setPublishError(null); } }}
                onPublish={handleConfirmPublish}
                defaultName={dashboardTitle}
                defaultDescription={dashboardDescription}
                initialContentTypeId={3}
                initialVersionLabel={nextVersionLabel}
                externalVersionError={publishError}
            />

            <MeasureWizardModal
                isOpen={isMeasureWizardOpen}
                onClose={() => setIsMeasureWizardOpen(false)}
                onSave={(newMeasure) => {
                    // Start of Update Logic
                    let updatedMeasures;
                    const exists = customMeasures.find(m => m.id === newMeasure.id);

                    if (exists) {
                        // Update
                        updatedMeasures = customMeasures.map(m => m.id === newMeasure.id ? newMeasure : m);
                    } else {
                        // Create
                        updatedMeasures = [...customMeasures, newMeasure];
                    }

                    setCustomMeasures(updatedMeasures);

                    // Auto-save measures to backend immediately (no screenshot needed)
                    saveMeasuresOnly(updatedMeasures);

                    // Sync with Enriched Dataset
                    if (enrichedDataset) {
                        // Replace or Add in enriched list
                        let newEnrichedMeasures = enrichedDataset.measures || [];
                        const enrichedExists = newEnrichedMeasures.find((m: any) => m.id === newMeasure.id || m.name === newMeasure.name);

                        if (enrichedExists) {
                            newEnrichedMeasures = newEnrichedMeasures.map((m: any) => (m.id === newMeasure.id || m.name === newMeasure.name) ? newMeasure : m);
                        } else {
                            newEnrichedMeasures = [...newEnrichedMeasures, newMeasure];
                        }

                        setEnrichedDataset({
                            ...enrichedDataset,
                            measures: newEnrichedMeasures
                        });
                    }
                }}
                onDeleteMeasure={(measureToDelete) => {
                    // Safety Check: Is it used in any widget?
                    const isUsed = widgetItems.some(w => {
                        const cfg = w.config || {};
                        const allConfigLists = [
                            ...(cfg.yAxis || []),
                            ...(cfg.series || []),
                            ...(cfg.value || []),
                            ...(cfg.target || []),
                            ...(cfg.comparison || []),
                            ...(cfg.min || []),
                            ...(cfg.max || []),
                            ...(cfg.columns || []),
                            ...(cfg.values || []),
                            ...(cfg.lineMeasures || []),
                            ...(cfg.forecast || []),
                            ...(cfg.confidence || [])
                        ];
                        // Also check simple top-level fields just in case
                        const isUsedDirectly = Object.values(cfg).includes(measureToDelete.name);
                        return isUsedDirectly || allConfigLists.some(f => f.name === measureToDelete.name);
                    });

                    if (isUsed) {
                        setMeasureUsageAlert({ isOpen: true, measureName: measureToDelete.name });
                        return;
                    }

                    // Set state to show the confirm modal
                    setMeasureToDelete(measureToDelete);
                }}
                existingMeasures={customMeasures}
                availableFields={(() => {
                    // If a Data Model is selected, derive columns from its tables.
                    // API returns DatabaseOut which has top-level `tables`; fall back to model_definition.tables for raw objects.
                    const dmTables: any[] | null = selectedDataModel?.tables?.length
                        ? selectedDataModel.tables
                        : selectedDataModel?.model_definition?.tables?.length
                            ? selectedDataModel.model_definition.tables
                            : null;

                    if (dmTables) {
                        const dims: any[] = [];
                        const msrs: any[] = [];
                        dmTables.forEach((table: any) => {
                            const tableName = table.name || table.alias || '';
                            const columns: any[] = Array.isArray(table.columns) ? table.columns : [];
                            columns.forEach((col: any) => {
                                const colName = typeof col === 'string' ? col : (col.name || col.alias || col.field || '');
                                if (!colName) return;
                                const fieldName = tableName ? `${tableName}.${colName}` : colName;
                                // Use explicit data_type if available (from ColumnSchema), else fallback to name heuristic
                                const dtype = typeof col === 'object' ? (col.data_type || col.dataType || '') : '';
                                const isNumber = dtype === 'number' || (!dtype && /amount|price|cost|value|revenue|profit|sales|qty|count|score/.test(colName.toLowerCase()) && !colName.toLowerCase().endsWith('id') && !colName.toLowerCase().endsWith('code'));
                                if (isNumber) {
                                    msrs.push({ name: fieldName, type: 'number' });
                                } else {
                                    dims.push({ name: fieldName, type: dtype || 'string' });
                                }
                            });
                        });
                        return { dimensions: dims, measures: [...msrs, ...customMeasures] };
                    }
                    // Fallback: derive from enriched dataset
                    return {
                        dimensions: enrichedDataset?.dimensions || [],
                        measures: enrichedDataset?.measures || []
                    };
                })()}
                initialData={measureToEdit}
            />

            {/* Header Settings Modal (canvas/style settings) */}
            <HeaderSettingsModal
                isOpen={isHeaderSettingsOpen}
                onClose={() => setIsHeaderSettingsOpen(false)}
                settings={{
                    title: dashboardTitle,
                    description: dashboardDescription,
                    logoUrl: logoUrl,
                    showTitle: showTitle,
                    showLogo: showLogo,
                    titleStyle: titleStyle,
                    canvas: canvasStyle
                }}
                onSave={handleHeaderSettingsSave}
            />

            {/* Dashboard Settings Dialog */}
            {isDashSettingsOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsDashSettingsOpen(false)} />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden flex flex-col" style={{ maxHeight: '90vh' }}>

                        {/* ── Header ── */}
                        <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center shadow-sm">
                                    <Settings className="h-4.5 w-4.5 text-blue-500" />
                                </div>
                                <div>
                                    <h2 className="text-[16px] font-bold text-gray-900 leading-tight">Dashboard Settings</h2>
                                    <p className="text-[11px] text-gray-400 mt-0.5">Configure appearance and visibility</p>
                                </div>
                            </div>
                            <button onClick={() => setIsDashSettingsOpen(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
                                <X className="w-4.5 h-4.5" />
                            </button>
                        </div>

                        {/* ── Scrollable Body ── */}
                        <div className="flex-1 overflow-y-auto px-7 py-6 space-y-0">

                            {/* ── Section 1: Identity ── */}
                            <div className="pb-6">
                                <div className="space-y-4">
                                    {/* Dashboard Name */}
                                    <div>
                                        <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1.5">Dashboard Name</label>
                                        <input type="text" value={editDashName} onChange={(e) => setEditDashName(e.target.value)}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm bg-white"
                                            placeholder="My Dashboard" autoFocus />
                                    </div>
                                    {/* Description */}
                                    <div>
                                        <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1.5">Description</label>
                                        <textarea value={editDashDesc} onChange={(e) => setEditDashDesc(e.target.value)}
                                            placeholder="e.g., Executive overview of Q3 revenue, regional performance, and key financial metrics..." rows={3}
                                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none shadow-sm bg-white" />
                                    </div>
                                </div>
                            </div>


                        </div>

                        {/* ── Footer ── */}
                        <div className="flex justify-between items-center gap-2 px-7 py-4 border-t border-gray-100 bg-gray-50/80 shrink-0">
                            <div className="text-[11px] text-gray-400">
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => setIsDashSettingsOpen(false)}
                                    className="px-4 py-2 text-[13px] font-medium text-gray-700 border border-gray-200 rounded-xl hover:bg-white transition-colors">
                                    Cancel
                                </button>
                                <button id="dashboard-settings-confirm" onClick={saveDashboardSettings} disabled={savingDashSettings || !editDashName.trim()}
                                    className="px-5 py-2 text-[13px] font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-sm">
                                    {savingDashSettings ? 'Saving…' : (!dashboardId ? 'Confirm & Save' : 'Save')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex-1 flex overflow-hidden relative">
                {isExportingPdf && (
                    <div className="absolute inset-0 z-[120] bg-white/70 backdrop-blur-[1px] flex items-center justify-center">
                        <div className="flex flex-col items-center gap-3 px-5 py-4 rounded-xl bg-white shadow-md border border-slate-200">
                            <div className="w-7 h-7 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
                            <p className="text-sm font-medium text-slate-700">Waiting for widget data and rendering before PDF export...</p>
                        </div>
                    </div>
                )}
                <style>{`
                    .react-grid-item > .react-resizable-handle {
                        position: absolute;
                        width: 20px;
                        height: 20px;
                        bottom: 0;
                        right: 0;
                        cursor: se-resize;
                        z-index: 9999 !important; /* Force handle on top */
                    }
                    .react-grid-item > .react-resizable-handle::after {
                        content: "";
                        position: absolute;
                        right: 3px;
                        bottom: 3px;
                        width: 5px;
                        height: 5px;
                        border-right: 2px solid rgba(0,0,0,0.4);
                        border-bottom: 2px solid rgba(0,0,0,0.4);
                    }
                    /* Placeholder Override */
                    .react-grid-placeholder {
                        background: #eff6ff !important; /* blue-50 */
                        border: 2px dashed #93c5fd !important; /* blue-300 */
                        opacity: 0.8 !important;
                        border-radius: 8px !important;
                        z-index: 10 !important;
                    }
                    /* Auto-hiding Scrolbar */
                    .hover-scrollbar {
                        scrollbar-width: thin;
                        scrollbar-color: transparent transparent;
                        transition: scrollbar-color 0.3s;
                    }
                    .hover-scrollbar:hover {
                        scrollbar-color: #cbd5e1 transparent;
                    }
                    .hover-scrollbar::-webkit-scrollbar {
                        width: 6px;
                        height: 6px;
                    }
                    .hover-scrollbar::-webkit-scrollbar-track {
                        background: transparent;
                    }
                    .hover-scrollbar::-webkit-scrollbar-thumb {
                        background-color: transparent;
                        border-radius: 20px;
                    }
                    .hover-scrollbar:hover::-webkit-scrollbar-thumb {
                        background-color: #cbd5e1;
                    }
                `}</style>

                {/* CANVAS AREA (Left) */}
                <div className="flex-1 relative flex flex-col min-w-0 group/canvas">


                    {/* Scrollable Canvas Content */}
                    <div
                        className="flex-1 overflow-y-auto relative bg-[#f0f0f0] dark:bg-slate-900/50 hover-scrollbar [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-transparent hover:[&::-webkit-scrollbar-thumb]:bg-slate-300 dark:hover:[&::-webkit-scrollbar-thumb]:bg-slate-600 [&::-webkit-scrollbar-track]:bg-transparent transition-colors"
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
                        onDrop={onWrapperDrop}
                        onClick={handleBackgroundClick}
                    >

                        <div
                            id="dashboard-canvas"
                            className="min-h-full transition-all duration-300 ease-in-out"
                            style={{
                                backgroundColor: canvasStyle?.backgroundColor || resolveColorTheme(dashboardColorTheme).canvasBg,
                                padding: canvasStyle?.margin !== undefined ? `${canvasStyle.margin}px` : '16px'
                            }}
                        >
                            <ResponsiveGridLayout
                                className="layout"
                                layouts={layouts}
                                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                                cols={colsMap} // Ultra-High Resolution (120 Cols)
                                rowHeight={5}  // Ultra-fine 5px height
                                onLayoutChange={handleLayoutChange}
                                onBreakpointChange={onBreakpointChange}
                                isDraggable={true}
                                isResizable={true}
                                resizeHandles={['se']} // Explicitly enable SE handle
                                isDroppable={false}
                                margin={[5, 5]} // Smaller margin for density
                                compactType={compactType} // Null for free placement
                                preventCollision={compactType === null} // Free Layout = No Pushing (prevents "flying away"). Auto = Pushing allowed.
                                draggableHandle=".draggable-handle"
                            >
                                {widgetItems.map((w, widgetIndex) => {
                                    const colorTheme = resolveColorTheme(dashboardColorTheme);
                                    const layoutStyles = getWidgetLayoutStyle(dashboardLayout, widgetIndex, colorTheme);
                                    const isSelected = selectedWidgetId === w.id;
                                    const containerStyle: React.CSSProperties = {
                                        ...layoutStyles.container,
                                        boxShadow: isSelected && !isSaving
                                            ? `0 0 0 2px #118DF0, 0 2px 8px rgba(0,0,0,0.12)`
                                            : layoutStyles.container.boxShadow,
                                    };

                                    return (
                                     <div
                                        key={w.id}
                                        data-grid={w.layout || { x: 0, y: 0, w: defaultWidth, h: defaultHeight }}
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent background click
                                            if (!isViewOnly) handleWidgetClick(e, w.id);
                                        }}
                                        className={`
                                            group transition-all relative
                                            ${(isSelected && !isSaving) ? 'ring-2 ring-blue-500 z-10' : 'ring-transparent z-1'}
                                        `}
                                        style={containerStyle}
                                    >
                                        {!isViewOnly && (
                                            <div
                                                className={`
                                                    absolute top-2 z-50 flex items-center gap-1 
                                                    transition-all duration-200 ease-in-out no-snapshot
                                                    ${selectedWidgetId === w.id ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0'}
                                                    right-2
                                                `}
                                            >
                                                <div className="flex items-center gap-1 p-1 rounded-lg bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm shadow-sm border border-slate-200 dark:border-slate-700">
                                                    {/* DRAG HANDLE (Move Icon) */}
                                                    <div
                                                        className="p-1.5 text-slate-400 hover:text-blue-500 rounded-md cursor-move draggable-handle transition-colors"
                                                        title="Move Widget"
                                                    >
                                                        <Move className="w-3.5 h-3.5" />
                                                    </div>

                                                    <div className="w-px h-3 bg-slate-200 dark:bg-slate-700 mx-0.5" />

                                                    {/* Agentic Insights */}
                                                    {w.config?.aiInsights && (
                                                        <button
                                                            className={`p-1.5 rounded-md transition-colors ${activeInsightWidgetId === w.id ? 'text-amber-500 bg-amber-50 dark:bg-amber-900/30' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/30'}`}
                                                            title="Toggle Agentic Insights"
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            onClick={(e) => { e.stopPropagation(); handleAIInsights(w.id); }}
                                                        >
                                                            <Lightbulb className="w-3.5 h-3.5" fill={activeInsightWidgetId === w.id ? "currentColor" : "none"} />
                                                        </button>
                                                    )}

                                                    {/* Duplicate */}
                                                    <button
                                                        className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md transition-colors"
                                                        title="Duplicate Widget"
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => duplicateWidget(e, w.id)}
                                                    >
                                                        <Copy className="w-3.5 h-3.5" />
                                                    </button>
                                                    {/* Delete */}
                                                    <button
                                                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors"
                                                        title="Delete Widget"
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => confirmDelete(e, w.id)}
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {/* WIDGET HEADER — shown when title text is non-empty.
                                             KPI & RAG-KPI are excluded: they render their own
                                             internal label, so the outer bar would be a duplicate. */}
                                        {!!w.config?.title && w.type !== 'pageHeader' && w.type !== 'kpi' && w.type !== 'ragKpi' && (
                                            <div
                                                className={`flex items-center select-none gap-2 transition-colors ${!isViewOnly ? 'cursor-move draggable-handle' : ''}`}
                                                style={{
                                                    ...layoutStyles.header,
                                                    height: `${w.config?.style?.titleHeight ?? 28}px`,
                                                    paddingLeft: `${w.config?.style?.titlePaddingX ?? 10}px`,
                                                    paddingRight: `${w.config?.style?.titlePaddingX ?? 10}px`,
                                                    // Widget-specific background override takes precedence
                                                    ...(w.config?.style?.backgroundColor ? {
                                                        backgroundColor: w.config.style.backgroundColor,
                                                        borderBottom: '1px solid rgba(0,0,0,0.05)',
                                                    } : {}),
                                                }}
                                            >
                                                <span
                                                    className={`truncate flex items-center gap-1.5 flex-1 w-full
                                                        ${
                                                            w.config?.style?.textAlign === 'center' ? 'justify-center' :
                                                            w.config?.style?.textAlign === 'right'  ? 'justify-end'    : 'justify-start'
                                                        }
                                                    `}
                                                    style={{
                                                        color:      w.config?.style?.color || (layoutStyles.showHeaderBg ? '#ffffff' : colorTheme.widgetText),
                                                        fontSize:   w.config?.style?.fontSize   ? `${w.config.style.fontSize}px` : '11px',
                                                        fontWeight: w.config?.style?.fontWeight || '600',
                                                        fontStyle:  w.config?.style?.fontStyle  || 'normal',
                                                    }}
                                                >
                                                    {w.config?.title}
                                                    {w.config?.style?.showChartName && (
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-blue-50 text-blue-500 border border-blue-100 shrink-0">
                                                            {CHART_TYPE_LABELS[w.type] || w.type}
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        )}

                                        <div className="flex-1 p-2 relative overflow-hidden">
                                            {/* RENDER ACTUAL CHART */}
                                            {/* RENDER ACTUAL CHART */}
                                            {w.type === 'filter' ? (
                                                <FilterWidget
                                                    widget={w}
                                                    dataset={enrichedDataset || selectedDataset}
                                                    dataModel={selectedDataModel}
                                                    globalFilters={globalFilters}
                                                    onFilterChange={handleFilterChange}
                                                    onWidgetUpdate={updateWidget}
                                                    isEditMode={!isViewOnly}
                                                />
                                            ) : (
                                                <WidgetRenderer
                                                    widget={w}
                                                    isSelected={selectedWidgetId === w.id}
                                                    onSelect={(e) => handleWidgetClick(e, w.id)}
                                                    onDelete={(e) => confirmDelete(e, w.id)}
                                                    onDuplicate={() => {
                                                        // Logic for duplication if needed
                                                    }}
                                                    onUpdate={updateWidget}
                                                    isEditMode={!isViewOnly}
                                                    dataset={enrichedDataset || selectedDataset} // Pass enriched if available
                                                    dataModel={selectedDataModel} // Pass selected data model for parquet queries
                                                    refreshTrigger={selectionTrigger} // Force re-render on selection
                                                    onAIInsights={() => handleAIInsights(w.id)}
                                                    isAIActive={activeInsightWidgetId === w.id}

                                                    // Cross-Filtering Props
                                                    globalFilters={globalFilters}
                                                    onFilterChange={handleFilterChange}

                                                    // AI Insights
                                                    showInsights={activeInsightWidgetId === w.id}
                                                    onToggleInsights={() => handleAIInsights(w.id)}

                                                    // Custom Calculation Engine
                                                    customMeasures={customMeasures}
                                                    isSnapshot={isSaving || isExportingPdf}
                                                    dashboardTheme={dashboardColorTheme}
                                                />
                                            )}
                                        </div>


                                    </div>
                                )})}
                            </ResponsiveGridLayout>
                            <Watermark />
                        </div>
                    </div>
                </div>

                {/* RIGHT SIDEBAR (Unified) */}
                <div className={`
                    bg-[#fafafa] border-l border-[#d6d6d6] flex flex-col z-20 shadow-lg transition-all duration-300 ease-in-out relative shrink-0
                    ${isSidebarOpen ? 'w-80' : 'w-0 border-l-0'}
                `}>
                    <div className="flex-1 w-full overflow-hidden flex flex-col h-full bg-[#fafafa] relative">
                        <div className="flex-1 min-h-0 relative pt-0 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-transparent hover:[&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-track]:bg-transparent transition-colors">
                            <WidgetToolbox
                                onWidgetAdd={(type, config) => addWidget(type, null, null, config)}
                                selectedWidget={selectedWidgetId ? widgetItems.find(w => w.id === selectedWidgetId) : null}
                                selectionTrigger={selectionTrigger}
                                onWidgetUpdate={updateWidget}
                                onDataModelChange={(model: any) => setSelectedDataModel(model)}
                                customMeasures={customMeasures}
                                initialDataModel={selectedDataModel}
                                isLocked={isDataModelLocked}
                                onEditMeasure={(measure: any) => {
                                    setMeasureToEdit(measure);
                                    setIsMeasureWizardOpen(true);
                                }}
                                onPreviewTable={(tableName) => {
                                    setPreviewStartTable(tableName);
                                    setIsPreviewModelOpen(true);
                                }}
                                onDeleteMeasure={(measureToDeleteParams: any) => {
                                    // Safety Check: Is it used in any widget?
                                    const isUsed = widgetItems.some(w => {
                                        const cfg = w.config || {};
                                        const allConfigLists = [
                                            ...(cfg.yAxis || []),
                                            ...(cfg.series || []),
                                            ...(cfg.value || []),
                                            ...(cfg.target || []),
                                            ...(cfg.comparison || []),
                                            ...(cfg.min || []),
                                            ...(cfg.max || []),
                                            ...(cfg.columns || []),
                                            ...(cfg.values || []),
                                            ...(cfg.lineMeasures || []),
                                            ...(cfg.forecast || []),
                                            ...(cfg.confidence || [])
                                        ];
                                        const isUsedDirectly = Object.values(cfg).includes(measureToDeleteParams.name);
                                        return isUsedDirectly || allConfigLists.some(f => f.name === measureToDeleteParams.name);
                                    });

                                    if (isUsed) {
                                        setMeasureUsageAlert({ isOpen: true, measureName: measureToDeleteParams.name });
                                        return;
                                    }

                                    // Set state to show the confirm modal
                                    setMeasureToDelete(measureToDeleteParams);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* MEASURE USAGE ALERT MODAL */}
            <ConfirmModal
                isOpen={measureUsageAlert.isOpen}
                title="Cannot Delete Measure"
                message={`Cannot delete '${measureUsageAlert.measureName}' because it is currently used by one or more widgets. Please remove it from the widgets first.`}
                confirmText="Got it"
                cancelText="Close"
                isDestructive={false}
                onConfirm={() => setMeasureUsageAlert({ isOpen: false, measureName: '' })}
                onCancel={() => setMeasureUsageAlert({ isOpen: false, measureName: '' })}
            />

            {/* CONFIRM DELETE MEASURE MODAL */}
            <ConfirmModal
                isOpen={!!measureToDelete}
                title="Delete Measure"
                message={`Are you sure you want to delete the measure '${measureToDelete?.name}'?`}
                confirmText="Delete"
                isDestructive={true}
                onConfirm={() => {
                    const toDelete = measureToDelete;
                    setMeasureToDelete(null);

                    // Remove from customMeasures
                    const updatedMeasures = customMeasures.filter(m => m.id !== toDelete.id);
                    setCustomMeasures(updatedMeasures);

                    // Auto-save measures to backend immediately
                    saveMeasuresOnly(updatedMeasures);

                    // Also remove from enrichedDataset if present
                    if (enrichedDataset) {
                        const newEnrichedMeasures = (enrichedDataset.measures || []).filter((m: any) => m.id !== toDelete.id && m.name !== toDelete.name);
                        setEnrichedDataset({
                            ...enrichedDataset,
                            measures: newEnrichedMeasures
                        });
                    }
                }}
                onCancel={() => setMeasureToDelete(null)}
            />

            {/* CONFIRM DELETE MODAL */}
            <ConfirmModal
                isOpen={!!widgetToDelete}
                title="Delete Widget"
                onConfirm={removeWidget}
                onClose={() => setWidgetToDelete(null)}
            >
                <div className="flex flex-col gap-2">
                    <span>Are you sure you want to delete the chart '{widgetItems.find(w => w.id === widgetToDelete)?.title || 'Selected Chart'}'?</span>
                    <span className="text-red-600 font-medium">This action cannot be undone and it will not be retrieved again.</span>
                </div>
            </ConfirmModal>

            {/* DATA PREVIEW MODAL */}
            <DataPreviewModal
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                dataset={selectedDataset}
            />

            {/* DATA MODEL PREVIEW TABS MODAL */}
            <DataModelPreviewTabsModal
                isOpen={isPreviewModelOpen}
                onClose={() => setIsPreviewModelOpen(false)}
                dataModel={selectedDataModel}
                initialTableName={previewStartTable}
            />

            {/* VERSION HISTORY PANEL */}
            {dashboardId && (
                <DashboardVersionHistoryPanel
                    dashboardId={dashboardId}
                    isOpen={versionHistoryOpen}
                    onClose={() => setVersionHistoryOpen(false)}
                    onLoadSnapshot={handleLoadSnapshot}
                />
            )}
        </div>
        </DashboardErrorBoundary>
    );
}
